import { AbstractValidatedClass, Joi, ValidatedClass, constraint } from '@verclam/form-validator';
import isBirthDateNotInTheFuture from '@UI/Pages/Beneficiaries/Sidebars/UpsertBeneficiarySidebar/FormData/constraint/isBirthDateNotInTheFuture';
import isNationalHealthIdentifierValid from '@UI/Pages/Beneficiaries/Sidebars/UpsertBeneficiarySidebar/FormData/constraint/isNationalHealthIdentifierValid';
import type PlainDate from '@Domain/PlainDate';
import type BeneficiaryView from '@UI/Store/Beneficiaries/Views/BeneficiaryView';

type BeneficiaryFormArgs = {
	firstName: string;
	lastName: string;
	birthName: string | null;
	birthDate: PlainDate | null;
	homeAddress: string | null;
	phoneNumber: string | null;
	nationalHealthIdentifier: string | null;
	assignedUserIds: readonly number[];
};

/**
 * `homeAddress` and `phoneNumber` carry no format constraint on purpose: international numbers,
 * country codes, extensions and separators vary too widely for a pattern to stay correct.
 *
 * `birthDate` carries a `PlainDate`, not the API's raw string: the sidebar builds it from the calendar
 * widget's selection, and the create/update Commands serialize it back with `toApi()` on their way out.
 */
@ValidatedClass({ ignoreNullValue: true })
class BeneficiaryFormData extends AbstractValidatedClass<BeneficiaryFormArgs> {
	@constraint(Joi.string().max(120), () => 'Le prénom ne doit pas dépasser 120 caractères.')
	@constraint(Joi.string().required(), () => 'Le prénom est obligatoire')
	public readonly firstName: string;

	@constraint(Joi.string().max(120), () => 'Le nom ne doit pas dépasser 120 caractères.')
	@constraint(Joi.string().required(), () => 'Le nom est obligatoire')
	public readonly lastName: string;

	@constraint(
		Joi.string().max(120).allow(''),
		() => 'Le nom de naissance ne doit pas dépasser 120 caractères.',
	)
	public readonly birthName: string | null;

	@constraint(isBirthDateNotInTheFuture, () => 'La date de naissance ne peut pas être dans le futur.')
	public readonly birthDate: PlainDate | null;

	@constraint(Joi.string().max(255).allow(''), () => 'L’adresse ne doit pas dépasser 255 caractères.')
	public readonly homeAddress: string | null;

	@constraint(
		Joi.string().max(30).allow(''),
		() => 'Le numéro de téléphone ne doit pas dépasser 30 caractères.',
	)
	public readonly phoneNumber: string | null;

	@constraint(
		isNationalHealthIdentifierValid,
		() => 'L’INS saisi est invalide. Vérifiez les 15 caractères et la clé de contrôle.',
	)
	public readonly nationalHealthIdentifier: string | null;

	// No constraint: an empty selection is valid, the connected professional may unassign themselves.
	public readonly assignedUserIds: readonly number[];

	constructor(data: BeneficiaryFormArgs) {
		super(data);
		this.firstName = data.firstName;
		this.lastName = data.lastName;
		this.birthName = data.birthName;
		this.birthDate = data.birthDate;
		this.homeAddress = data.homeAddress;
		this.phoneNumber = data.phoneNumber;
		this.nationalHealthIdentifier = data.nationalHealthIdentifier;
		this.assignedUserIds = data.assignedUserIds;
		Object.freeze(this);
	}

	/**
	 * `initialAssignedUserIds` lets the creation panel pre-select the connected professional: the
	 * edition panel always goes through `fromView` instead, never this one.
	 */
	static createEmpty(initialAssignedUserIds: readonly number[] = []): BeneficiaryFormData {
		return new BeneficiaryFormData({
			firstName: '',
			lastName: '',
			birthName: null,
			birthDate: null,
			homeAddress: null,
			phoneNumber: null,
			nationalHealthIdentifier: null,
			assignedUserIds: initialAssignedUserIds,
		});
	}

	static fromView(beneficiary: BeneficiaryView): BeneficiaryFormData {
		return new BeneficiaryFormData({
			firstName: beneficiary.firstName,
			lastName: beneficiary.lastName,
			birthName: beneficiary.birthName,
			birthDate: beneficiary.birthDate,
			homeAddress: beneficiary.homeAddress,
			phoneNumber: beneficiary.phoneNumber,
			nationalHealthIdentifier: beneficiary.nationalHealthIdentifier,
			assignedUserIds: beneficiary.assignedUserIds,
		});
	}

	setFirstName(firstName: string): this {
		return this.cloneWith({ firstName } as Partial<BeneficiaryFormArgs>);
	}

	setLastName(lastName: string): this {
		return this.cloneWith({ lastName } as Partial<BeneficiaryFormArgs>);
	}

	setBirthName(birthName: string): this {
		return this.cloneWith({ birthName } as Partial<BeneficiaryFormArgs>);
	}

	setBirthDate(birthDate: PlainDate | null): this {
		return this.cloneWith({ birthDate } as Partial<BeneficiaryFormArgs>);
	}

	setHomeAddress(homeAddress: string): this {
		return this.cloneWith({ homeAddress } as Partial<BeneficiaryFormArgs>);
	}

	setPhoneNumber(phoneNumber: string): this {
		return this.cloneWith({ phoneNumber } as Partial<BeneficiaryFormArgs>);
	}

	setNationalHealthIdentifier(nationalHealthIdentifier: string): this {
		return this.cloneWith({ nationalHealthIdentifier } as Partial<BeneficiaryFormArgs>);
	}

	setAssignedUserIds(assignedUserIds: readonly number[]): this {
		return this.cloneWith({ assignedUserIds } as Partial<BeneficiaryFormArgs>);
	}
}

export default BeneficiaryFormData;
